import { Typography } from 'antd'
import { createStyles } from 'antd-style'

const { Text } = Typography

type Props = {
  /** Value to render and (optionally) copy. */
  children: string
  /** Show the antd copy-to-clipboard control after the value. Default: true. */
  copyable?: boolean
  /** Single-line truncate with full value in a hover tooltip. Default: wraps. */
  ellipsis?: boolean
  /** Override the body font size. Default: `12`. */
  fontSize?: number
  /** Copy a different text than the one displayed (e.g. show `ws_123`, copy `123`). */
  copyText?: string
}

const useStyles = createStyles(({ token }) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%',
    minWidth: 0,
    paddingInline: 6,
    paddingBlock: 1,
    borderRadius: token.borderRadiusSM,
    background: token.colorFillTertiary,
    border: `1px solid ${token.colorBorderSecondary}`,
    fontFamily: token.fontFamilyCode,
    color: token.colorText,
    lineHeight: 1.5,
    verticalAlign: 'middle',

    '& .ant-typography-copy': {
      flex: '0 0 auto',
      marginInlineStart: 2,
      color: token.colorTextTertiary
    },

    '& .ant-typography-copy:hover': {
      color: token.colorPrimary
    }
  },

  truncate: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  wrap: {
    minWidth: 0,
    wordBreak: 'break-all'
  }
}))

/**
 * Mono-styled value chip — URLs, IDs, tokens. The chip is `inline-flex`, so
 * the value text and the copy button always stay aligned and never break
 * apart across lines (unlike a raw `<Text copyable code>`, which renders
 * inline and lets the copy button drop to a new line when the value wraps).
 *
 * Use everywhere a copyable mono-spaced value is displayed (`Descriptions`
 * cells, headers, audit metadata, OIDC endpoints, webhook URLs, …) — don't
 * re-roll the `<Text copyable code style={{ fontSize: 12 }}>` pattern inline.
 */
export const MonoValue = ({
  children,
  copyable = true,
  copyText,
  ellipsis,
  fontSize = 12
}: Props) => {
  const { styles, cx } = useStyles()

  return (
    <span className={styles.root} style={{ fontSize }}>
      <span
        className={cx(ellipsis ? styles.truncate : styles.wrap)}
        title={ellipsis ? children : undefined}
      >
        {children}
      </span>
      {copyable ? <Text copyable={{ text: copyText ?? children }} /> : null}
    </span>
  )
}
